import {
  type MutableRefObject,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { ApiError } from "@/core/api/types";
import {
  useGetAiScriptSpreadsheet,
  useLoadAiScriptSpreadsheetSheet,
} from "@/payroll/ai-script/query";
import { isAiSpreadsheetSheetEditable } from "@/payroll/ai-script/utils";
import type { WorkbookReadSource } from "@/payroll/ai-script/workbook-read-source";
import { getSheetApproval } from "@/payroll/approvals/utils";
import type { IPayrollSheetApprovalStatus } from "@/payroll/payroll.d";
import { adaptSpreadsheetSheet } from "./adapter";
import { openHyperlinkInNewTab } from "./hyperlink";
import {
  buildSpreadsheetRegionRowMap,
  hasActiveSpreadsheetRegionView,
  projectAdaptedSpreadsheetSheet,
} from "./spreadsheet-region-view";
import { useGridViewportPreservation } from "./use-grid-viewport-preservation";
import type { ViewerUiState } from "./use-viewer-ui-state";
import type { UseWorkbookHistoryResult } from "./use-workbook-history";
import { emptySelection } from "./utils";
import {
  EMPTY_REGION_OVERRIDE_REVIEWS,
  EMPTY_REGION_VIEW_STATES,
  EMPTY_SPREADSHEET_REGIONS,
} from "./viewer-helpers";

interface UseViewerActiveSheetParams {
  data: ReturnType<typeof useGetAiScriptSpreadsheet>["data"];
  businessId: string | null;
  runId: string;
  readOnlySource: WorkbookReadSource | undefined;
  approvalStatus: IPayrollSheetApprovalStatus | null | undefined;
  isLocked: boolean;
  isMobile: boolean;
  gridWrapperRef: RefObject<HTMLDivElement | null>;
  history: UseWorkbookHistoryResult;
  workbookVersionRef: MutableRefObject<number | undefined>;
  ui: ViewerUiState;
}

export const useViewerActiveSheet = ({
  data,
  businessId,
  runId,
  readOnlySource,
  approvalStatus,
  isLocked,
  isMobile,
  gridWrapperRef,
  history,
  workbookVersionRef,
  ui,
}: UseViewerActiveSheetParams) => {
  const {
    activeSheetId,
    selectSheet,
    setPendingCell,
    setGridSelection,
    setRowMenu,
    setCommentCard,
    setColumnHeaderMenu,
    setSpreadsheetColumnMenu,
    setRegionPreview,
    setRegionDialogTarget,
    regionViewStatesBySheet,
  } = ui;
  const spreadsheet = data?.spreadsheet;
  const sheets = spreadsheet?.sheets ?? [];

  const activeSheet =
    sheets.find((sheet) => sheet.id === activeSheetId) ?? sheets[0];
  const activeSheetValue = activeSheet?.id ?? "";
  const activeSheetApproval = getSheetApproval(activeSheet, approvalStatus);
  const shouldLoadActiveSheet = !!activeSheet && !activeSheet.empty;
  const {
    data: activeSheetData,
    error: activeSheetError,
    refetch: refetchActiveSheet,
    isFetching: isFetchingActiveSheet,
  } = useLoadAiScriptSpreadsheetSheet(
    businessId,
    runId,
    activeSheet?.id ?? null,
    shouldLoadActiveSheet,
    readOnlySource,
  );

  const activeLoadedSheet =
    activeSheetData?.sheet && activeSheetData.sheet.id === activeSheet?.id
      ? activeSheetData.sheet
      : null;
  const frozenRow = activeLoadedSheet?.frozen_row ?? null;
  const frozenColumn = activeLoadedSheet?.frozen_column ?? null;
  const loadedSheetId = activeLoadedSheet?.id;
  const previousLayoutRef = useRef<{
    sheetId: string;
    frozenRow: number | null;
  } | null>(null);

  // Reset display coordinates only when a loaded sheet's boundary changes.
  // Pending navigation uses saved coordinates and must survive sheet loading.
  useEffect(() => {
    const previous = previousLayoutRef.current;
    previousLayoutRef.current = loadedSheetId
      ? { sheetId: loadedSheetId, frozenRow }
      : null;
    if (
      !loadedSheetId ||
      previous?.sheetId !== loadedSheetId ||
      previous.frozenRow === frozenRow
    ) {
      return;
    }
    setGridSelection(emptySelection);
  }, [loadedSheetId, frozenRow, setGridSelection]);

  const { cancelViewportRestore, clearGridViewport, handleGridScrollCapture } =
    useGridViewportPreservation({
      activeSheet: activeLoadedSheet,
      gridWrapperRef,
    });

  const changeActiveSheet = (sheetId: string) => {
    clearGridViewport();
    selectSheet(sheetId);
    setPendingCell(null);
    setGridSelection(emptySelection);
    setRowMenu(null);
    setCommentCard(null);
    setColumnHeaderMenu(null);
    setSpreadsheetColumnMenu(null);
    setRegionPreview(null);
    setRegionDialogTarget(null);
    history.reset();
    workbookVersionRef.current = undefined;
  };

  const isActiveSheetEditable =
    isAiSpreadsheetSheetEditable(activeSheet) && !isLocked;
  const isActiveSheetMissing =
    activeSheetError instanceof ApiError && activeSheetError.status === 404;
  const canMutateActiveSheet = isActiveSheetEditable && !activeSheetError;

  useEffect(() => {
    if (canMutateActiveSheet) return;
    setRegionDialogTarget(null);
    setRegionPreview(null);
  }, [canMutateActiveSheet, setRegionDialogTarget, setRegionPreview]);
  const isTooNarrowForGrid = isMobile;

  const baseAdaptedSheet = useMemo(
    () =>
      activeLoadedSheet
        ? adaptSpreadsheetSheet(activeLoadedSheet, {
            onHyperlinkClick: openHyperlinkInNewTab,
          })
        : null,
    [activeLoadedSheet],
  );
  const activeRegions = activeLoadedSheet?.regions ?? EMPTY_SPREADSHEET_REGIONS;
  const activeRegionOverrideReviews =
    activeLoadedSheet?.region_override_reviews ?? EMPTY_REGION_OVERRIDE_REVIEWS;
  const workbookRegionOverrideReviews =
    spreadsheet?.region_override_reviews ?? EMPTY_REGION_OVERRIDE_REVIEWS;
  const activeRegionViewStates =
    regionViewStatesBySheet[activeSheetValue] ?? EMPTY_REGION_VIEW_STATES;
  const regionRowMap = useMemo(
    () =>
      baseAdaptedSheet && activeLoadedSheet
        ? buildSpreadsheetRegionRowMap({
            rowCount: baseAdaptedSheet.rowCount,
            frozenRow,
            cells: activeLoadedSheet.cells,
            regions: activeRegions,
            viewStates: activeRegionViewStates,
          })
        : [],
    [
      activeLoadedSheet,
      activeRegionViewStates,
      activeRegions,
      baseAdaptedSheet,
      frozenRow,
    ],
  );
  const adaptedSheet = useMemo(
    () =>
      baseAdaptedSheet
        ? projectAdaptedSpreadsheetSheet(baseAdaptedSheet, regionRowMap)
        : null,
    [baseAdaptedSheet, regionRowMap],
  );
  const hasActiveRegionView = Object.values(activeRegionViewStates).some(
    hasActiveSpreadsheetRegionView,
  );

  return {
    spreadsheet,
    sheets,
    activeSheet,
    activeSheetValue,
    activeSheetApproval,
    activeSheetError,
    isActiveSheetMissing,
    refetchActiveSheet,
    isFetchingActiveSheet,
    activeLoadedSheet,
    frozenColumn,
    frozenRow,
    cancelViewportRestore,
    clearGridViewport,
    handleGridScrollCapture,
    changeActiveSheet,
    isActiveSheetEditable,
    canMutateActiveSheet,
    isTooNarrowForGrid,
    activeRegions,
    activeRegionOverrideReviews,
    workbookRegionOverrideReviews,
    activeRegionViewStates,
    regionRowMap,
    adaptedSheet,
    hasActiveRegionView,
  };
};

export type ViewerActiveSheet = ReturnType<typeof useViewerActiveSheet>;
